// src/pages/Coleta/components/ColetaDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { useBootstrap } from '../../../context/BootstrapContext';
import { getTowers } from '../../../infra/api/Torres';
import { atualizarDadosColeta, mensagemErro, registrarColeta } from '../../../infra/api/Coletas';
import {
    PAGINAS_COLETA,
    TURNOS,
    codigoSugerido,
    dataDoExperimento,
    deInputDataHora,
    paraInputDataHora,
    rotuloAlvo,
    turnoPelaHora,
} from '../../../utils/coleta';

/**
 * Registro de amostra nas páginas de coleta.
 *
 * - `novo`: análise → coleta → dispositivo. A amostra fica aguardando o tubo; quando
 *   ele entra no dispositivo escolhido, o experimento nasce ligado a ela.
 * - `experimento`: análise → coleta, para um experimento que nasceu sem registro.
 * - `editar`: só os dados da coleta de uma amostra já registrada.
 */
export default function ColetaDialog({
    open,
    modo = 'novo',
    pagina,
    config,
    unidade,
    pontos,
    origens,
    experimento,
    linha,
    pontoInicial,
    onClose,
    onSalvo,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { bootstrap } = useBootstrap();
    const meta = PAGINAS_COLETA[pagina];

    const passos = useMemo(() => {
        if (modo === 'editar') return ['Coleta'];
        if (modo === 'experimento') return ['Análise', 'Coleta'];
        return ['Análise', 'Coleta', 'Dispositivo'];
    }, [modo]);
    const [passo, setPasso] = useState(0);
    const nomePasso = passos[passo];

    const [codigo, setCodigo] = useState('');
    const [dataHora, setDataHora] = useState('');
    const [turno, setTurno] = useState('');
    const [turnoEditado, setTurnoEditado] = useState(false);
    const [pontoColetaId, setPontoColetaId] = useState('');
    const [origem, setOrigem] = useState('');
    const [temperatura, setTemperatura] = useState('');
    const [torreId, setTorreId] = useState('');

    const [torres, setTorres] = useState([]);
    const [carregandoTorres, setCarregandoTorres] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    // Estado inicial a cada abertura.
    useEffect(() => {
        if (!open) return;
        const coleta = linha?.coleta;
        const quando = coleta?.dataHora ? new Date(coleta.dataHora) : new Date();
        setPasso(0);
        setCodigo(linha?.identificacao || codigoSugerido(meta.prefixoCodigo, quando));
        setDataHora(paraInputDataHora(quando));
        setTurno(coleta?.turno || turnoPelaHora(quando));
        setTurnoEditado(Boolean(coleta?.turno));
        setPontoColetaId(coleta?.pontoColetaId || '');
        setOrigem(coleta?.origem || '');
        setTemperatura(
            Number.isFinite(linha?.temperaturaAmostra) ? String(linha.temperaturaAmostra).replace('.', ',') : ''
        );
        setTorreId('');
        setErro('');
    }, [open, linha, meta]);

    // Ponto vindo do cartão dos Pontos de coleta (só se ele vale para esta página). Num
    // efeito à parte porque a lista de pontos pode chegar depois de o diálogo abrir; só
    // preenche o que ainda está vazio, sem desfazer uma escolha do usuário.
    useEffect(() => {
        if (!open || linha || !pontoInicial) return;
        const ponto = pontos.find((p) => p.id === pontoInicial);
        if (!ponto) return;
        setPontoColetaId((atual) => atual || ponto.id);
        setOrigem((atual) => atual || ponto.local || '');
    }, [open, linha, pontoInicial, pontos]);

    // Dispositivos livres: só no registro novo, ao chegar no passo do dispositivo.
    useEffect(() => {
        if (!open || nomePasso !== 'Dispositivo') return;
        let ativo = true;
        setCarregandoTorres(true);
        getTowers(0, 200, { running: false })
            .then((res) => ativo && setTorres(res?.content || []))
            .catch(() => ativo && setTorres([]))
            .finally(() => ativo && setCarregandoTorres(false));
        return () => {
            ativo = false;
        };
    }, [open, nomePasso]);

    const protocolos = bootstrap?.protocolos;
    const nomeProtocolo = (id) => (protocolos || []).find((p) => p.id === id)?.name || 'outro protocolo';

    // Os do protocolo da página primeiro: os demais trocam de protocolo ao registrar.
    const torresOrdenadas = useMemo(
        () =>
            [...torres].sort(
                (a, b) =>
                    Number(b?.protocol?.id === config?.protocoloId) - Number(a?.protocol?.id === config?.protocoloId)
            ),
        [torres, config?.protocoloId]
    );
    const torreEscolhida = torres.find((t) => t.id === torreId);
    const trocaProtocolo = torreEscolhida && torreEscolhida?.protocol?.id !== config?.protocoloId;

    const quando = deInputDataHora(dataHora);
    const temperaturaNumero = temperatura.trim() === '' ? null : Number(temperatura.replace(',', '.'));
    const temperaturaInvalida = temperaturaNumero !== null && !Number.isFinite(temperaturaNumero);
    const coletaValida = Boolean(quando && turno) && !temperaturaInvalida;

    const mudarDataHora = (texto) => {
        setDataHora(texto);
        const data = deInputDataHora(texto);
        // Enquanto o usuário não escolheu o turno, ele acompanha a hora.
        if (data && !turnoEditado) setTurno(turnoPelaHora(data));
    };

    const dadosColeta = () => ({
        dataHora: quando.toISOString(),
        turno,
        pontoColetaId: pontoColetaId || null,
        origem: origem.trim() || null,
        temperaturaAmostra: temperaturaNumero,
    });

    const podeAvancar =
        (nomePasso === 'Análise') ||
        (nomePasso === 'Coleta' && coletaValida) ||
        (nomePasso === 'Dispositivo' && Boolean(torreId));
    const ultimoPasso = passo === passos.length - 1;

    const concluir = async () => {
        setSalvando(true);
        setErro('');
        try {
            const resultado =
                modo === 'editar'
                    ? await atualizarDadosColeta(linha.id, dadosColeta())
                    : await registrarColeta({
                          pagina,
                          identificacao: codigo.trim() || null,
                          coleta: dadosColeta(),
                          torreId: modo === 'novo' ? torreId : null,
                          experimentId: modo === 'experimento' ? experimento.id : null,
                      });
            onSalvo(resultado);
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível salvar. Tente novamente.'));
        } finally {
            setSalvando(false);
        }
    };

    // Cada modo diz o que de fato acontece: só o `novo` cria uma análise que espera tubo.
    const inicioEnsaio = dataDoExperimento(experimento?.inicio);
    const cabecalho = {
        novo: {
            titulo: `Nova análise · ${meta.nomeCurto}`,
            subtitulo: 'Registre o essencial. Complete os detalhes quando puder.',
        },
        experimento: {
            titulo: `Registrar dados · ${meta.nomeCurto}`,
            subtitulo: [
                `Ensaio de ${experimento?.nome || experimento?.serialNumber || 'dispositivo'}`,
                inicioEnsaio && `iniciado em ${inicioEnsaio.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`,
            ].filter(Boolean).join(' · '),
        },
        editar: { titulo: 'Dados da coleta', subtitulo: linha?.identificacao },
    }[modo];

    return (
        <Dialog open={open} onClose={salvando ? undefined : onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
            {/* Cabeçalho */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2 }}>
                <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex' }}>
                    <Lucide.FlaskConical size={22} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} sx={{ fontSize: '1.05rem' }} noWrap>{cabecalho.titulo}</Typography>
                    <Typography variant="body2" color="text.secondary">{cabecalho.subtitulo}</Typography>
                </Box>
                <IconButton onClick={onClose} disabled={salvando} aria-label="Fechar">
                    <Lucide.X size={20} />
                </IconButton>
            </Stack>

            {passos.length > 1 && (
                <Stepper activeStep={passo} sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                    {passos.map((p) => (
                        <Step key={p}>
                            <StepLabel>{p}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                {nomePasso === 'Análise' && (
                    <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Lucide.CircleCheck size={22} color={theme.palette.primary.main} style={{ flexShrink: 0, marginTop: 2 }} />
                                <Box>
                                    <Typography fontWeight={700}>{config?.analiseNome}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {rotuloAlvo(config?.alvo)} · {unidade}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Protocolo {config?.protocoloNome}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                        {modo === 'experimento' && (
                            <Alert severity="info" icon={<Lucide.Link size={18} />}>
                                Os dados vão para o ensaio que já rodou. Use a data e a hora reais da
                                coleta, antes do início do ensaio.
                            </Alert>
                        )}
                    </Stack>
                )}

                {nomePasso === 'Coleta' && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Código da amostra"
                                value={codigo}
                                onChange={(e) => setCodigo(e.target.value)}
                                disabled={modo === 'editar'}
                                helperText={modo === 'editar' ? ' ' : 'Se já existir, recebe um sufixo.'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Ordenha / turno"
                                value={turno}
                                onChange={(e) => {
                                    setTurno(e.target.value);
                                    setTurnoEditado(true);
                                }}
                                helperText={turnoEditado ? ' ' : 'Sugerido pela hora da coleta.'}
                            >
                                {TURNOS.map((t) => (
                                    <MenuItem key={t.valor} value={t.valor}>{t.rotulo}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                options={origens}
                                inputValue={origem}
                                onInputChange={(_, valor) => setOrigem(valor)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Origem da amostra" placeholder="Ex.: Linha de ordenha / tanque 01" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Ponto de coleta"
                                value={pontoColetaId}
                                onChange={(e) => {
                                    const id = e.target.value;
                                    setPontoColetaId(id);
                                    // O local cadastrado do ponto vira a origem, se ainda não há uma.
                                    const local = pontos.find((p) => p.id === id)?.local;
                                    if (local && !origem.trim()) setOrigem(local);
                                }}
                                helperText={pontos.length === 0 ? 'Nenhum ponto cadastrado.' : 'Cadastro de Pontos de coleta.'}
                            >
                                <MenuItem value="">
                                    <em>Sem ponto</em>
                                </MenuItem>
                                {pontos.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Data e hora da coleta"
                                value={dataHora}
                                onChange={(e) => mudarDataHora(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                error={!quando}
                                helperText="Horário local."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Temperatura da amostra"
                                value={temperatura}
                                onChange={(e) => setTemperatura(e.target.value)}
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                                error={temperaturaInvalida}
                                helperText={temperaturaInvalida ? 'Use só números, ex.: 3,8' : 'Opcional.'}
                            />
                        </Grid>
                    </Grid>
                )}

                {nomePasso === 'Dispositivo' && (
                    <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Chip size="small" label={config?.analiseNome} color="primary" variant="outlined" sx={{ mb: 1 }} />
                            <Typography fontWeight={800} sx={{ fontSize: '1.25rem' }}>{codigo || '—'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {[pontos.find((p) => p.id === pontoColetaId)?.nome, origem.trim()].filter(Boolean).join(' · ') || 'Sem ponto e origem'}
                            </Typography>
                            {quando && (
                                <Typography variant="body2" color="text.secondary">
                                    {quando.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                </Typography>
                            )}
                        </Paper>

                        <TextField
                            select
                            fullWidth
                            label="Dispositivo"
                            value={torreId}
                            onChange={(e) => setTorreId(e.target.value)}
                            disabled={carregandoTorres}
                            InputProps={carregandoTorres ? { endAdornment: <CircularProgress size={18} sx={{ mr: 3 }} /> } : undefined}
                            helperText={!carregandoTorres && torres.length === 0 ? 'Nenhum dispositivo livre no momento.' : ' '}
                        >
                            {torresOrdenadas.map((t) => (
                                <MenuItem key={t.id} value={t.id}>
                                    <Stack>
                                        <span>{t.tag ? `${t.tag} · ${t.numeroSerie}` : t.numeroSerie}</span>
                                        {t?.protocol?.id !== config?.protocoloId && (
                                            <Typography variant="caption" color="text.secondary">
                                                Está em {nomeProtocolo(t?.protocol?.id)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </MenuItem>
                            ))}
                        </TextField>

                        {trocaProtocolo && (
                            <Alert severity="warning" icon={<Lucide.RefreshCw size={18} />}>
                                Ao registrar, o dispositivo passa para o protocolo <strong>{config?.protocoloNome}</strong>.
                                A troca vale a partir do próximo tubo inserido.
                            </Alert>
                        )}
                        <Alert severity="info" icon={<Lucide.Info size={18} />}>
                            Insira o tubo neste dispositivo. A análise aparece em andamento assim que o ensaio começar.
                        </Alert>
                    </Stack>
                )}

                {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
                {passo > 0 && (
                    <Button onClick={() => setPasso((p) => p - 1)} disabled={salvando} color="inherit" variant="outlined">
                        Voltar
                    </Button>
                )}
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!podeAvancar || salvando}
                    onClick={ultimoPasso ? concluir : () => setPasso((p) => p + 1)}
                    sx={{ minWidth: 140 }}
                >
                    {salvando ? 'Salvando...' : ultimoPasso ? (modo === 'editar' ? 'Salvar' : 'Registrar') : 'Continuar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
